import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import { useSnackbar } from '../../components/GlobalSnackbar'
import { apiLabourDeactivate, apiLabourDeactivatePreview, SwitchOffPreview } from '@src/apis/labour-deactivate'

// Shows the shifts and crews someone comes off before they are switched off. Nothing changes until
// the user confirms. beforeSwitchOff lets the edit dialog save its other fields first, because that
// save rewrites the person's crews.
function SwitchOffDialog({
  profile,
  beforeSwitchOff,
  onCancel,
  onSwitchedOff,
}: {
  profile: { serialId: number, firstName: string, lastName: string },
  beforeSwitchOff?(): Promise<any>,
  onCancel(): void,
  onSwitchedOff(): void,
}) {
  const { alert } = useSnackbar()
  const [preview, setPreview] = useState<SwitchOffPreview | null>(null)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const name = `${profile.firstName} ${profile.lastName}`

  useEffect(() => {
    let isCurrent = true
    apiLabourDeactivatePreview(profile.serialId)
      .then(result => {
        if (isCurrent) setPreview(result.data.preview)
      })
      .catch(() => {
        if (!isCurrent) return
        alert({ message: `Could not load the shifts for ${name}. They have not been switched off.`, type: 'error' })
        onCancel()
      })
    return () => { isCurrent = false }
  }, [profile.serialId])

  const handleConfirm = async () => {
    setIsSaving(true)
    try {
      if (beforeSwitchOff) await beforeSwitchOff()
      const result = await apiLabourDeactivate(profile.serialId)
      const { ukgFailed = [] } = result.data || {}
      if (ukgFailed.length > 0) {
        alert({ message: `${name} is switched off, but UKG would not delete shifts ${ukgFailed.join(', ')}. Please ask payroll to remove them.`, type: 'error' })
      } else {
        alert({ message: `${name} switched off.` })
      }
      onSwitchedOff()
    } catch (e) {
      // A timeout lands here too, and the API may still finish, so don't claim nothing changed.
      setIsSaving(false)
      alert({ message: `Could not confirm ${name} was switched off. Please refresh and check before trying again.`, type: 'error' })
    }
  }

  const renderContent = () => {
    if (!preview) return <Spinner size={32} />
    const { shifts, crews } = preview
    return <React.Fragment>
      {shifts.length > 0
        ? <DialogContentText>
          They will be taken off {shifts.length === 1 ? 'this shift' : `these ${shifts.length} shifts`} and out of UKG. Switching them back on later will not put them back.
        </DialogContentText>
        : <DialogContentText>They are not on any upcoming shifts.</DialogContentText>
      }
      {shifts.length > 0 && <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>DRS</TableCell>
            <TableCell>Project</TableCell>
            <TableCell>Depot</TableCell>
            <TableCell>Crew</TableCell>
            <TableCell>In UKG</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shifts.map(shift => <TableRow key={shift.drsId}>
            <TableCell>{moment(shift.date).format('DD/MM/YYYY')}</TableCell>
            <TableCell>{shift.drsId}</TableCell>
            <TableCell>{shift.project}</TableCell>
            <TableCell>{shift.depot}</TableCell>
            <TableCell>{shift.crew}</TableCell>
            <TableCell>{shift.inUkg ? 'Yes' : 'No'}</TableCell>
          </TableRow>)}
        </TableBody>
      </Table>}
      {crews.length > 0 && <CrewText>
        They will also be removed from {crews.length === 1 ? 'crew' : `${crews.length} crews`}: {crews.join(', ')}.
      </CrewText>}
    </React.Fragment>
  }

  return <Dialog open onClose={isSaving ? undefined : onCancel} maxWidth='md' fullWidth aria-labelledby='switch-off-dialog-title'>
    <DialogTitle id='switch-off-dialog-title'>Switch off {name}?</DialogTitle>
    <DialogContent>{renderContent()}</DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={isSaving}>Cancel</Button>
      <Button variant='contained' color='primary' onClick={handleConfirm} disabled={!preview || isSaving}>
        {isSaving ? 'Switching off' : 'Switch off'}
      </Button>
    </DialogActions>
  </Dialog>
}

const Spinner = styled(CircularProgress)`
  display: block;
  margin: 16px auto;
`
const CrewText = styled(DialogContentText)`
  margin-top: 16px;
`

export default SwitchOffDialog
